import {branch as branchHOC, renderNothing} from 'recompose'

import compose from 'lodash/flowRight'
import * as React from 'react'
import {Suspense} from 'react'
import {connect} from 'react-redux'
import {graphql} from 'react-relay'

import withHook from '../../../../../hocs/withHook'
import withStatusSubscription from '../../../../../hocs/withStatusSubscription'
import {useAppSelector} from '../../../../../hooks/react-redux'
import {useRefetchableLazyLoadQuery} from '../../../../../hooks/relay/useRefetchableLazyLoadQuery'
import type {State} from '../../../../../reducers/types'
import {getBuildStatsAfterAndBeforeBuildLocator} from '../../../../../rest/locators'
import {getBuild, getBuildType, makeBuildBranchSelector} from '../../../../../selectors'
import type {Branch} from '../../../../../services/rest'
import {subscribeOnBuildTypeEvents} from '../../../../../utils/subscriber'
import * as SubscriptionEvents from '../../../../../utils/subscriptionEvents'
import BuildTypeInfoWrapper from '../../../../common/BuildTypeInfoWrapper/BuildTypeInfoWrapper'
import VisibleContainer from '../../../../common/Visible/Visible'

import type {BuildTypeInfoPopupQuery} from './__generated__/BuildTypeInfoPopupQuery.graphql'
import BuildTypeInfoPopup, {TIMELINE_STAT_COUNT} from './BuildTypeInfoPopup'
import type {
  InnerProps,
  OwnPropsWrapper,
  Props,
  PropsWrapper,
  StatePropsWrapper,
} from './BuildTypeInfoPopup.types'

const BuildTypeInfoPopupContainer = withStatusSubscription<InnerProps>()(BuildTypeInfoPopup)

const Events = [
  SubscriptionEvents.BUILD_STARTED,
  SubscriptionEvents.BUILD_FINISHED,
  SubscriptionEvents.BUILD_CHANGED_STATUS,
  SubscriptionEvents.BUILD_INTERRUPTED,
  SubscriptionEvents.BUILD_REMOVED,
]
const query = graphql`
  query BuildTypeInfoPopupQuery($locator: String!) {
    builds(locator: $locator) {
      ...BuildsTimelineFragment
    }
  }
`
function BuildTypeInfoPopupFetcher(props: Props) {
  const {buildTypeId, buildId, locator} = props
  const [{builds}, refetch] = useRefetchableLazyLoadQuery<BuildTypeInfoPopupQuery>(query, {
    locator,
  })
  const internalId = useAppSelector(state => getBuildType(state, buildTypeId)?.internalId)
  const hasBuildId = buildId != null
  React.useEffect(
    () =>
      internalId != null && hasBuildId
        ? subscribeOnBuildTypeEvents(internalId, Events, refetch)
        : undefined,
    [internalId, hasBuildId, refetch, locator],
  )
  return <BuildTypeInfoPopupContainer {...props} buildsKey={builds} />
}

function BuildTypeInfoPopupWrapper({
  locator,
  className,
  buildId,
  buildTypeId,
  branch,
}: PropsWrapper) {
  const wrapper = React.useMemo(() => {
    const props = {
      key: locator,
      locator,
      className,
      buildId,
      buildTypeId,
      branch,
    }
    return (
      <Suspense
        fallback={
          <BuildTypeInfoWrapper>
            <BuildTypeInfoPopupContainer {...props} loading />
          </BuildTypeInfoWrapper>
        }
      >
        <BuildTypeInfoWrapper>
          <BuildTypeInfoPopupFetcher {...props} />
        </BuildTypeInfoWrapper>
      </Suspense>
    )
  }, [branch, buildId, buildTypeId, className, locator])
  return <VisibleContainer>{() => wrapper}</VisibleContainer>
}

export default compose(
  React.memo,
  withHook((props: OwnPropsWrapper) => {
    const {buildId} = props
    const buildBranchSelector = React.useMemo(makeBuildBranchSelector, [])
    const branch = useAppSelector(state => buildBranchSelector(state, buildId))

    return {
      branch,
    }
  }),
  React.memo,
  connect(
    (
      state: State,
      {buildTypeId, buildId, branch}: OwnPropsWrapper & {branch: Branch},
    ): StatePropsWrapper => {
      const {state: buildState} = getBuild(state, buildId) ?? {}

      const locator =
        buildId != null
          ? getBuildStatsAfterAndBeforeBuildLocator(
              buildTypeId,
              buildId,
              branch,
              TIMELINE_STAT_COUNT,
            )
          : ''
      return {
        locator,
        branch,
        queued: buildState === 'queued',
      }
    },
  ),
  branchHOC((props: PropsWrapper) => props.queued, renderNothing),
  React.memo,
)(BuildTypeInfoPopupWrapper)
